// Polls the org's "check all mobile numbers" task and keeps a progress toast open while it runs
import {
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { IMobileNumberCheck, MobileNumberCheckStatus } from "shared";

import { useFetchMobileNumberCheckQuery } from "../services/api/mobileNumberCheckApi/mobileNumberCheck";
import { emptySplitApi } from "../services/api/baseApi";
import { CacheTags } from "../services/types/cacheTags";

import { useGetSelectedOrganisation } from "./useGetSelectedOrganisation";
import { useModal } from "./useModals";

const IDLE_POLL_MS = 60_000;
const IN_PROGRESS_POLL_MS = 2_000;
const TOAST_ID = `mobile_number_check`;

function isRunning(check: IMobileNumberCheck | null) {
  return !!check && check.status !== MobileNumberCheckStatus.FAILED;
}

/** Status of the org's check. Shares the cache entry polled by useMobileNumberCheckToast */
export function useMobileNumberCheck() {
  const currentOrg = useGetSelectedOrganisation();
  const { currentData } = useFetchMobileNumberCheckQuery(currentOrg?.id, { skip: !currentOrg });
  const check = currentData?.check || null;

  return {
    runningCheck: isRunning(check) ? check : null,
    failedCheck: check?.status === MobileNumberCheckStatus.FAILED ? check : null,
    /** Result of the last successful check, null once it is over an hour old */
    lastRun: currentData?.lastRun || null,
  };
}

/** Mount once (Root). Owns the polling cadence, and the toast that stands in while the progress modal is closed */
export function useMobileNumberCheckToast() {
  const currentOrg = useGetSelectedOrganisation();
  const dispatch = useDispatch();
  const wasRunning = useRef(false);
  const failedTokenShown = useRef<string | null>(null);
  const { state: modalOpen } = useModal(`mobileNumberCheck`);
  const [pollingInterval, setPollingInterval] = useState(IDLE_POLL_MS);

  // `currentData` so another org's in-progress check is never shown after switching org
  const { currentData } = useFetchMobileNumberCheckQuery(currentOrg?.id, {
    skip: !currentOrg,
    pollingInterval,
  });
  const check = currentData?.check || null;
  const running = isRunning(check);
  const failed = check?.status === MobileNumberCheckStatus.FAILED;

  // Poll fast while queued / in progress, a failed entry lingers in redis so it goes back to idle
  useEffect(() => {
    setPollingInterval(running ? IN_PROGRESS_POLL_MS : IDLE_POLL_MS);
  }, [running]);

  useEffect(() => {
    if (modalOpen) {
      // The modal shows progress, result and failure itself
      toast.dismiss(TOAST_ID);

      if (failed) {
        failedTokenShown.current = check.token;
      }
    }
    else if (running) {
      // react-toastify closes a controlled toast once progress reaches 1, so hold just short until the entry is gone
      const options = {
        progress: Math.min(check.progress, 0.99),
        type: `default` as const,
        autoClose: false as const,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
      };

      if (toast.isActive(TOAST_ID)) {
        toast.update(TOAST_ID, { render: check.progressMessage, ...options });
      }
      else {
        toast(check.progressMessage, { toastId: TOAST_ID, ...options });
      }
    }
    else if (failed) {
      // The failed entry stays in redis for a few minutes, only show each failure once
      if (failedTokenShown.current !== check.token) {
        failedTokenShown.current = check.token;

        // `progress: undefined` hands the bar back to the autoClose timer
        const options = {
          progress: undefined,
          type: `error` as const,
          autoClose: 10_000,
          closeButton: true,
          closeOnClick: true,
        };

        if (toast.isActive(TOAST_ID)) {
          toast.update(TOAST_ID, { render: check.progressMessage, ...options });
        }
        else {
          toast(check.progressMessage, { toastId: TOAST_ID, ...options });
        }
      }
    }
    else if (wasRunning.current) {
      // Running -> no entry means it finished, failures always leave a failed entry behind
      toast.update(TOAST_ID, {
        render: `Mobile number check complete! ${currentData?.lastRun?.invalidContactCount ?? 0} contacts have an invalid mobile number`,
        type: `success`,
        progress: undefined,
        autoClose: 3000,
        closeButton: true,
        closeOnClick: true,
      });
    }

    if (wasRunning.current && !running) {
      // Invalid flags have changed, a failed run may still have updated some
      dispatch(emptySplitApi.util.invalidateTags([CacheTags.Contacts, CacheTags.SingleContact]));
    }

    wasRunning.current = running;
  }, [check?.progressMessage, check?.progress, check?.token, running, failed, modalOpen]);
}
